import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import moment from 'moment';

interface ContractWorkPlaceIntervention {
  id?: string;
  contractId: string;
  contractInterventionSettingId: string;
  workPlaceId: string;
  systemTypeId: string;
  period?: string;
  freeInterventionsCount: number;
  usedInterventionsCount: number;
  freeInterventionsCountReset: number;
  freeHours: number;
  usedHours: number;
  freeHoursReset: number;
  workPlace?: any;
  systemType?: any;
}

interface ContractInterventionSetting {
  id?: string;
  contractId: string;
  enumContractInterventionTypeId: number;
  enumContractInterventionTariffTypeId: number;
  interventionLevel: 'contract' | 'workPlace' | 'workPlaceSystemTypePeriod';
  freeHoursLimit?: number;
  customHourlyRate?: number;
  freeInterventionsCount?: number;
  enumContractInterventionType: any;
  enumContractInterventionTariffType: any;
  contractWorkPlaceInterventions: ContractWorkPlaceIntervention[];
}

interface ContractState {
  id: string;
  accountManagerId: string;
  accountSaleId: string;
  companyId: string;
  offerId: string;
  enumContractStatusId: number;
  number: string;
  date: string;
  dateStart: string;
  dateEnd: string;
  datePeriod: number;
  acquisitionType: string;
  isExpedite: boolean;
  hasAutomaticExtension: boolean;
  intervention: number;
  hasUnlimitedInterventions: any;
  haveFile: boolean;
  file: string;
  registrationNumber: string;
  remarks: string;
  metadata: any;
  company: any;
  accountManager: any;
  accountSale: any;
  enumContractStatus: any;
  contractWorkPlaceItems: any[];
  contractLaborExtras: any[];
  contractCommentsTab: any[];
  location: any[];
  workPlaceIntervention: any[];
  contractInterventionSetting: ContractInterventionSetting;
  equipmentTab: {
    [key: string]: any;
  };
  laborTab: {
    [key: string]: any;
  };
  props: {
    [key: string]: any;
  };
}

const currentDate = moment().format('YYYY-MM-DD');
const currentDateEnd = moment().add(1, 'year').format('YYYY-MM-DD');

const initialState: ContractState = {
  id: '',
  accountManagerId: '',
  accountSaleId: '',
  companyId: '',
  offerId: '',
  enumContractStatusId: 1,
  number: '',
  date: currentDate,
  dateStart: currentDate,
  dateEnd: currentDateEnd,
  datePeriod: 0,
  acquisitionType: 'transfer bancar',
  isExpedite: false,
  hasAutomaticExtension: false,
  intervention: 0,
  hasUnlimitedInterventions: false,
  haveFile: false,
  file: '',
  registrationNumber: '',
  remarks: '',
  metadata: null,
  company: null,
  accountManager: null,
  accountSale: null,
  enumContractStatus: null,
  contractWorkPlaceItems: [],
  contractLaborExtras: [],
  contractCommentsTab: [],
  location: [],
  workPlaceIntervention: [],
  equipmentTab: { workPlaceId: null },
  laborTab: { laborPriceOnHour: 300, interventionPerSystemType: 1, interventionPriceOnRequest: 300 },
  props: { searchTerm: '', skip: 1, take: 10, totalItems: 0 },
  contractInterventionSetting: {
    id: '',
    contractId: '',
    enumContractInterventionTypeId: 1,
    enumContractInterventionTariffTypeId: 1,
    interventionLevel: 'contract',
    freeHoursLimit: 0,
    customHourlyRate: 180,
    freeInterventionsCount: 0,
    enumContractInterventionType: null,
    enumContractInterventionTariffType: null,
    contractWorkPlaceInterventions: [],
  },
};

const contractSlice = createSlice({
  name: 'contract',
  initialState,
  reducers: {
    setContract: (state, action: PayloadAction<Partial<ContractState>>) => {
      return { ...state, ...action.payload };
    },
    updateField: (state: any, action: PayloadAction<{ field: any; value: any }>) => {
      const { field, value } = action.payload;
      state[field] = value;
    },
    updateEquipmentTab: (state, action: PayloadAction<{ field: string; value: any }>) => {
      const { field, value } = action.payload;

      state.equipmentTab = {
        ...state.equipmentTab,
        [field]: value,
      };
    },
    updateLaborTab: (state, action: PayloadAction<{ field: string; value: any }>) => {
      const { field, value } = action.payload;

      state.laborTab = {
        ...state.laborTab,
        [field]: value,
      };
    },
    addContractWorkPlaceItem: (state, action) => {
      state.contractWorkPlaceItems.push(action.payload);
    },
    // addContractWorkPlaceIntervention: (state, action) => {
    //   const location = action.payload;
    //   if (!state.contractWorkPlaceInterventions?.find((loc) => loc.id === location.id)) {
    //     state.contractWorkPlaceInterventions?.push(location);
    //   }
    // },
    // removeContractWorkPlaceIntervention: (state: any, action: PayloadAction<any>) => {
    //   state.contractWorkPlaceInterventions = state.contractWorkPlaceInterventions?.filter(
    //     (loc: any) => loc.id !== action.payload
    //   );
    // },
    addLocation: (state, action) => {
      const location = action.payload;
      if (!state.location.find((loc) => loc.id === location.id)) {
        state.location.push(location);
      }
    },
    removeLocation: (state, action: PayloadAction<string>) => {
      state.location = state.location.filter((loc) => loc.id !== action.payload);
    },
    updateContractWorkPlaceItem: (state, action: PayloadAction<{ index: number; updates: any }>) => {
      const { index, updates } = action.payload;
      if (state.contractWorkPlaceItems[index]) {
        state.contractWorkPlaceItems[index] = {
          ...state.contractWorkPlaceItems[index],
          ...updates,
        };
      }
    },
    removeContractWorkPlaceItem: (state, action: PayloadAction<number>) => {
      const index = action.payload;
      if (index >= 0 && index < state.contractWorkPlaceItems.length) {
        state.contractWorkPlaceItems.splice(index, 1);
      }
    },
    addContractLaborExtra: (state, action) => {
      state.contractLaborExtras.push(action.payload);
    },
    addContractComment: (state, action: PayloadAction<any>) => {
      state.contractCommentsTab.push(action.payload);
    },
    updateContractComment: (
      state,
      action: PayloadAction<{
        departmentName: string;
        updates: Partial<any>;
      }>
    ) => {
      const { departmentName, updates } = action.payload;
      const index = state.contractCommentsTab.findIndex((comment) => comment.departmentName === departmentName);

      if (index !== -1) {
        state.contractCommentsTab[index] = {
          ...state.contractCommentsTab[index],
          ...updates,
        };
      }
    },
    removeContractComment: (
      state,
      action: PayloadAction<{
        departmentName: string;
        fileUid: string;
      }>
    ) => {
      const { departmentName, fileUid } = action.payload;
      const commentIndex = state.contractCommentsTab.findIndex((comment) => comment.departmentName === departmentName);

      if (commentIndex !== -1) {
        const comment = state.contractCommentsTab[commentIndex];

        comment.contractCommentFiles = comment.contractCommentFiles.filter(
          (file: any) => file.uid !== fileUid && file.id !== fileUid
        );

        state.contractCommentsTab[commentIndex] = {
          ...comment,
          contractCommentFiles: comment.contractCommentFiles,
        };
      }
    },
    updateProps: (state, action: PayloadAction<{ field: string; value: any }>) => {
      const { field, value } = action.payload;
      state.props = {
        ...state.props,
        [field]: value,
      };
    },
  },
});

export const {
  setContract,
  updateField,
  updateEquipmentTab,
  updateLaborTab,
  addContractWorkPlaceItem,
  updateContractWorkPlaceItem,
  removeContractWorkPlaceItem,
  addContractLaborExtra,
  addContractComment,
  updateContractComment,
  removeContractComment,
  addLocation,
  removeLocation,
  updateProps,
} = contractSlice.actions;

export default contractSlice.reducer;
